import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import {
  selectAuthStatus,
  sendMagicLinkAsync,
  saveMagicLinkEmail,
} from "../../../features/authentication/authenticationSlice";
import { selectBrandInfo } from "../../../features/brand/brandSlice";

import "./loginLinkRequest.css";

import {
  Container,
  Padding,
  Layout,
  Text,
  Sheet,
  Input,
  Button,
  AnchorLink,
  Form,
  Header,
} from "@kidslivesafe/sc-react";
import { useNavigate, useLocation } from "react-router-dom";

import Logo from "@assets/images/brand-logo.svg";
import HeaderLogo from "@assets/images/brand-logo--header.svg";

import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";

const LoginLinkRequest: React.FC = () => {
  /* -- local state -- */
  const [email, setEmail] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [emailError, setEmailError] = useState<boolean>(false);

  /* -- redux state -- */
  const authStatus = useAppSelector(selectAuthStatus);
  const brandInfo = useAppSelector(selectBrandInfo);

  /* -- setup hooks -- */
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  /* -- location state -- */
  const { magicLinkExpired } = location.state || {};

  const currentBreakPoint = useCurrentBreakpoint();

  /* -- handle submit -- */
  const handleSendLoginLink = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
  ) => {
    if (e) {
      e.preventDefault();
    }

    if (!email) {
      setErrorMessage("Please fill out the entire form.");
      setEmailError(true);
      return;
    } else if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setErrorMessage("Please enter vaild email.");
      setEmailError(true);
      return;
    }
    try {
      dispatch(saveMagicLinkEmail(email));
      const sendMagicLinkResponse = await dispatch(
        sendMagicLinkAsync({ email }),
      );
      navigate("/login-link-request/confirmation");
    } catch (error) {}
  };

  const renderLogo = () => {
    switch (brandInfo.acronym) {
      case "sf":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "147" : "88"}
            src={Logo}
            alt="Logo"
          />
        );
      case "pdc":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={Logo}
            alt="Logo"
          />
        );
      case "pis":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={HeaderLogo}
            alt="Logo"
          />
        );
      case "spr":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={HeaderLogo}
            alt="Logo"
          />
        );
      case "prr":
        return <img width="147" src={HeaderLogo} alt="Logo" />;
      case "kls":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={Logo}
            alt="Logo"
          />
        );
      case "qpr":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={Logo}
            alt="Logo"
          />
        );
      case "pf":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={HeaderLogo}
            alt="Logo"
          />
        );
      default:
        return (
          <img
            width={currentBreakPoint === "desktop" ? "147" : "88"}
            src={Logo}
            alt="Logo"
          />
        );
    }
  };

  return (
    <>
      {brandInfo.acronym === "qpr" ? (
        <Header
          homeUrl="/"
          displayButtons={false}
          menuButtonLinks={[]}
          logoUrl={HeaderLogo}
        ></Header>
      ) : (
        <Sheet>
          <Layout
            type="flex"
            flexProps={{ direction: "column", alignItems: "flex-start" }}
          >
            <Padding space="md">
              <div className="page-login-link-brand-logo-container">
                <Text color="white">{renderLogo()}</Text>
              </div>
            </Padding>
          </Layout>
        </Sheet>
      )}
      <Sheet>
        <Padding space="md">
          <div
            className={
              currentBreakPoint === "desktop"
                ? "page-login-link-sheet-form-container"
                : ""
            }
          >
            <Layout type="flex" flexProps={{ direction: "column", gap: "lg" }}>
              <Container
                bodyBorderRadius
                border={brandInfo.acronym === "qpr" ? true : false}
                customClassName="page-login-link-container"
                altColor="login"
              >
                <Padding
                  space={["pf"].includes(brandInfo.acronym) ? "xl" : "md"}
                >
                  <Form>
                    <Layout
                      type="flex"
                      flexProps={{
                        direction: "column",
                        gap: "md",
                      }}
                    >
                      <Layout
                        type="flex"
                        flexProps={{
                          direction: "column",
                          gap: "lg",
                        }}
                      >
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "column",
                            gap: "xs",
                          }}
                        >
                          <Text tag="h1" unresponsive>
                            We'll email you a login link
                          </Text>
                          <Text size="sm">
                            Get a secure link sent to your email to sign in. No
                            password needed.
                          </Text>
                        </Layout>
                        {magicLinkExpired ? (
                          <Container
                            customClassName="page-login-link_error-container"
                            altColor="error"
                          >
                            <Padding space="sm" top="md" bottom="md">
                              <Layout
                                type="flex"
                                flexProps={{
                                  direction: "column",
                                  gap: "xs",
                                }}
                              >
                                <Text
                                  fontWeight="bold"
                                  size="sm"
                                  textAlign="left"
                                  color="error"
                                >
                                  Oops, this link has expired
                                </Text>
                                <Text size="sm" textAlign="left">
                                  This login link is no longer valid. For your
                                  security, our links only stay active for 15
                                  minutes. You can request a new one below.
                                </Text>
                              </Layout>
                            </Padding>
                          </Container>
                        ) : null}
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "column",
                            gap: "md",
                          }}
                        >
                          <Input
                            id="login-email"
                            type="email"
                            value={email}
                            handleChange={setEmail}
                            placeholder="Enter Email"
                            handleError={() => {
                              setEmailError(false);
                            }}
                            error={emailError}
                            enableLastPass
                          />
                          {emailError && errorMessage && (
                            <Layout
                              type="flex"
                              flexProps={{ alignSelf: "flex-start" }}
                            >
                              <Text size="xs" color="error">
                                {errorMessage}
                              </Text>
                            </Layout>
                          )}

                          <Button
                            buttonText="Send me a login link"
                            color="primary"
                            handleClick={handleSendLoginLink}
                            disabled={authStatus === "loading"}
                            size="md"
                            fill="fill"
                            passThroughClassess="page-login-link__button-border"
                          />
                        </Layout>
                      </Layout>
                      <Layout
                        type="flex"
                        flexProps={{
                          alignItems: "center",
                          direction: "column",
                          gap: "lg",
                        }}
                      >
                        <AnchorLink href="/login" target="_self" size="xs">
                          Back to regular login
                        </AnchorLink>
                        <Layout
                          type="flex"
                          flexProps={{
                            alignItems: "center",
                            direction: "column",
                            gap: "xxs",
                          }}
                        >
                          <Text size="xs">
                            Your login link will be good for 15 minutes
                          </Text>
                        </Layout>
                      </Layout>
                    </Layout>
                  </Form>
                </Padding>
              </Container>
            </Layout>
          </div>
        </Padding>
      </Sheet>
    </>
  );
};

export default LoginLinkRequest;
